import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Clock, Newspaper, ArrowRight, Pencil, Trash2, X, MessageCircle, MoreVertical, Maximize2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import { SectionHelp } from "@/components/help";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { NewsItem } from "@shared/schema";
import { ReactionBar, CommentSection, BookmarkButton, ShareDialog } from "@/components/news";
import CreateNewsForm from "@/components/news/CreateNewsForm";
import { useReportTileEmpty } from "./DashboardTile";

interface NewsItemWithAuthor extends NewsItem {
  author?: { id: number; name: string; photoUrl?: string | null } | null;
}

export default function LatestNews() {
  const { data: newsItems, isLoading } = useQuery<NewsItemWithAuthor[]>({
    queryKey: ["/api/news"],
  });
  const [, setLocation] = useLocation();
  const [selectedNews, setSelectedNews] = useState<NewsItemWithAuthor | null>(null);

  const featuredNews = newsItems?.[0];
  const otherNews = newsItems?.slice(1, 5) ?? [];

  // Report emptiness so the dashboard can demote this tile when there's no news.
  useReportTileEmpty("latest_news", !isLoading && !featuredNews);

  return (
    <Card className="h-full flex flex-col overflow-hidden border border-border shadow-sm bg-card">
      <CardHeader className="pb-3 border-b border-border flex flex-row items-center justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 shadow-md shrink-0">
            <Newspaper className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <SectionHelp contentId="dashboard:news" tooltip="Learn about the news feed">
              <CardTitle className="text-lg font-bold text-foreground">Latest News</CardTitle>
            </SectionHelp>
            <p className="text-xs text-muted-foreground truncate">Company announcements &amp; updates</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 gap-1 font-semibold h-8 px-2 shrink-0"
          onClick={() => setLocation("/news")}
        >
          View All
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="p-4 flex-1 min-h-0">
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full">
            <div className="lg:col-span-3"><NewsFeaturedSkeleton /></div>
            <div className="lg:col-span-2 flex flex-col gap-2">
              <NewsRowSkeleton />
              <NewsRowSkeleton />
              <NewsRowSkeleton />
            </div>
          </div>
        ) : featuredNews ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full">
            {/* Featured hero */}
            <div
              onClick={() => setSelectedNews(featuredNews)}
              className="lg:col-span-3 group relative overflow-hidden rounded-xl cursor-pointer h-44 lg:h-auto lg:min-h-[180px] border border-border"
            >
              {featuredNews.imageUrl ? (
                <img
                  src={featuredNews.imageUrl}
                  alt={featuredNews.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500/20 via-orange-500/15 to-amber-500/20 flex items-center justify-center">
                  <Newspaper className="h-12 w-12 text-muted-foreground/50" />
                </div>
              )}
              {/* Legibility gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
              <Badge className="absolute top-3 left-3 bg-white/15 text-white border-white/25 backdrop-blur-sm hover:bg-white/25">
                {featuredNews.department || "Company"}
              </Badge>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="text-white font-bold text-base lg:text-lg leading-snug line-clamp-2 drop-shadow-sm">
                  {featuredNews.title}
                </h3>
                <div className="mt-2 flex items-center gap-3 text-white/80 text-xs">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDistanceToNow(new Date(featuredNews.publishDate), { addSuffix: true })}
                  </span>
                  {featuredNews.author?.name && (
                    <span className="truncate max-w-[45%]">by {featuredNews.author.name}</span>
                  )}
                  {!!featuredNews.comments && (
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-3.5 w-3.5" />
                      {featuredNews.comments}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Side list */}
            <div className="lg:col-span-2 flex flex-col gap-1.5 min-w-0">
              {otherNews.length > 0 ? (
                otherNews.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedNews(item)}
                    className="group flex gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/60 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      {item.imageUrl ? (
                        <div className="w-14 h-14 rounded-lg overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                      ) : (
                        <div className="w-14 h-14 bg-gradient-to-br from-muted to-muted/50 rounded-lg flex items-center justify-center">
                          <Newspaper className="h-5 w-5 text-muted-foreground/60" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground text-sm line-clamp-2 leading-snug group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">
                        {item.title}
                      </h4>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(item.publishDate), { addSuffix: true })}
                        </span>
                        {item.department && (
                          <span className="truncate text-muted-foreground/70">· {item.department}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-muted-foreground py-4">
                  <Newspaper className="h-6 w-6 mb-1 opacity-40" />
                  <p className="text-xs">No other stories yet</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center mb-3">
              <Newspaper className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">No news items available</p>
            <p className="text-sm text-muted-foreground mt-1">Check back later for updates</p>
          </div>
        )}
      </CardContent>

      <DashboardNewsModal
        news={selectedNews}
        onClose={() => setSelectedNews(null)}
      />
    </Card>
  );
}

function NewsFeaturedSkeleton() {
  return <Skeleton className="w-full h-full min-h-[180px] rounded-xl" />;
}

function NewsRowSkeleton() {
  return (
    <div className="flex gap-3 p-2">
      <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

// Full-featured News Modal for Dashboard
interface DashboardNewsModalProps {
  news: NewsItemWithAuthor | null;
  onClose: () => void;
}

function DashboardNewsModal({ news, onClose }: DashboardNewsModalProps) {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const commentSectionRef = useRef<HTMLDivElement>(null);

  // Record view when modal opens
  const recordViewMutation = useMutation({
    mutationFn: async (newsId: number) => {
      await apiRequest("POST", `/api/news/${newsId}/view`, {
        timeSpentSeconds: 0,
        scrollDepthPercent: 0,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/news/${news!.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/news'] });
      toast({ title: "Deleted", description: "News post deleted successfully." });
      onClose();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (news && user) {
      recordViewMutation.mutate(news.id);
    }
  }, [news?.id, user?.id]);

  if (!news) return null;

  return (
    <>
      <Dialog open={!!news} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col overflow-hidden [&>button:last-child]:hidden">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-xl font-semibold text-foreground">
                    {news.title}
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200">
                      {news.department || "Company"}
                    </Badge>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDistanceToNow(new Date(news.publishDate), { addSuffix: true })}
                    </div>
                    {news.author && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <span>by</span>
                        <span className="font-medium text-foreground">{news.author.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setIsEditOpen(true)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setIsDeleteOpen(true)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full hover:bg-muted"
                    onClick={() => {
                      onClose();
                      setLocation(`/news/${news.id}`);
                    }}
                    title="Open full page"
                  >
                    <Maximize2 className="h-4 w-4" />
                    <span className="sr-only">Open full page</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full hover:bg-muted"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </Button>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {news.imageUrl && (
              <div className="w-full h-64 bg-muted rounded-lg overflow-hidden mb-6">
                <img
                  src={news.imageUrl}
                  alt={news.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="prose prose-sm dark:prose-invert max-w-none mb-6">
              <div
                dangerouslySetInnerHTML={{ __html: news.content }}
                className="text-foreground leading-relaxed"
              />
            </div>

            {/* Tags */}
            {news.tags && news.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {news.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="bg-muted">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}

            <Separator className="my-6" />

            {/* Comments */}
            <div ref={commentSectionRef}>
              <CommentSection newsId={news.id} initialCommentCount={news.comments || 0} />
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 z-10 bg-background border-t border-border px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <ReactionBar newsId={news.id} compact />
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 h-8 px-2"
                  onClick={() => {
                    commentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="text-sm">{news.comments || 0}</span>
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <BookmarkButton newsId={news.id} showLabel />
                <ShareDialog newsId={news.id} title={news.title} showLabel />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {isEditOpen && (
        <CreateNewsForm
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          newsItem={news}
        />
      )}

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete News Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{news.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
